import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CancelledCustomerCardPayment,
  CheckedCustomerCardPayment,
  CreateCustomerCashPaymentInput,
  CreateCustomerCheckPaymentInput,
  CreateCustomerInput,
  CreatedCustomerPosCart,
  Customer,
  CustomerBillingDefaults,
  CustomerBillingSettingsInput,
  CustomerIdInput,
  CustomerIndexFilters,
  CustomerIndexResponseFor,
  CustomerOccasionReminderInput,
  CustomerPaymentAllocationInput,
  CustomerPaymentMethodSetupIntent,
  CustomerPaymentResult,
  CustomerPaymentSnapshotPreview,
  CustomerPaymentSnapshotSelection,
  CustomerPaymentStatus,
  CustomerPosHistoryItem,
  CustomerRecipientSuggestion,
  CustomerRecipientSuggestionFilters,
  CustomerSavedCards,
  CustomerSavedCardsResult,
  CustomerStatement,
  DeletedCustomerOccasionReminder,
  DuplicateCustomerPhoneInput,
  DuplicateCustomerPhoneResult,
  PrepareCustomerCardPaymentInput,
  PreparedCustomerCardPayment,
  SavedCustomerOccasionReminder,
  SendCustomerCardPaymentToReaderInput,
  SendCustomerReviewInviteResult,
  SendCustomerStatementResult,
  StoreCustomerPaymentMethodInput,
  UpdateCustomerInput,
  UpdateCustomerInternalNotesInput,
} from './customers.types'

export interface CustomerOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one customer. */
export interface CustomerScope extends CustomerOrganizationScope {
  customerId?: CustomerIdInput | null | undefined
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type CustomerQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export type CustomerIndexQueryKeyEntries<
  TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined,
> = CustomerOrganizationScope & {
  filters?: TFilters | undefined
}

export type CustomerPosHistoryQueryKeyEntries = CustomerScope & { limit?: number | undefined }

export type CustomerPaymentMethodsQueryKeyEntries = CustomerScope & {
  paymentEntrySessionId?: number | null | undefined
}

export type CustomerRecipientSuggestionsQueryKeyEntries = CustomerScope & {
  filters?: CustomerRecipientSuggestionFilters | undefined
}

export interface CustomerPaymentScope extends CustomerScope {
  paymentId?: CustomerIdInput | null | undefined
}

export interface CustomerOccasionReminderScope extends CustomerScope {
  reminderId?: CustomerIdInput
}

export async function getCustomers<
  TFilters extends CustomerIndexFilters | undefined = CustomerIndexFilters | undefined,
>({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: CustomerQueryContext<CustomerIndexQueryKeyEntries<TFilters>>) {
  return (
    (await axios.get<CustomerIndexResponseFor<TFilters>>(
      '/customers',
      buildAdminRequestConfig({
        organizationId,
        params: { ...filters },
        ...(signal ? { signal } : {}),
      })
    )) || []
  )
}

export async function getCustomer({
  queryKey: [{ organizationId, customerId }],
}: CustomerQueryContext<CustomerScope>) {
  const response = await axios.get<Customer>(
    `/customers/${customerId}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

export async function getCustomerBillingDefaults({
  queryKey: [{ organizationId }],
}: CustomerQueryContext<CustomerOrganizationScope>) {
  return (
    (await axios.get<CustomerBillingDefaults>(
      '/customers/billing-defaults',
      buildAdminRequestConfig({ organizationId })
    )) || {}
  )
}

export async function getDuplicateCustomerByPhone({
  organizationId,
  phone,
  excludeCustomerId,
}: DuplicateCustomerPhoneInput & CustomerOrganizationScope) {
  return axios.get<DuplicateCustomerPhoneResult>(
    '/customers/duplicate-phone',
    buildAdminRequestConfig({
      organizationId,
      params: {
        phone,
        ...(excludeCustomerId ? { exclude_customer_id: excludeCustomerId } : {}),
      },
    })
  )
}

export async function getCustomerReceivables({
  queryKey: [{ organizationId, customerId }],
}: CustomerQueryContext<CustomerScope>) {
  return axios.get<CustomerStatement>(
    `/customers/${customerId}/receivables`,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to the printable statement HTML. */
export async function printCustomerStatement({
  queryKey: [{ organizationId, customerId }],
}: CustomerQueryContext<CustomerScope>) {
  return axios.get<string>(
    `/customers/${customerId}/statement/print`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCustomer({
  organizationId,
  ...values
}: CreateCustomerInput & CustomerOrganizationScope) {
  return axios.post<Customer>('/customers', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateCustomer({
  organizationId,
  customerId,
  ...values
}: UpdateCustomerInput & CustomerScope) {
  return axios.patch<Customer>(
    `/customers/${customerId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCustomerBillingSettings({
  organizationId,
  customerId,
  ...values
}: CustomerBillingSettingsInput & CustomerScope) {
  return axios.patch<Customer>(
    `/customers/${customerId}/billing-settings`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCustomerInternalNotes({
  organizationId,
  customerId,
  internal_notes,
}: UpdateCustomerInternalNotesInput & CustomerScope) {
  return axios.patch<Customer>(
    `/customers/${customerId}/internal-notes`,
    { internal_notes },
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function softDeleteCustomer({ organizationId, customerId }: CustomerScope) {
  return axios.delete<string>(
    `/customers/${customerId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCustomerPosCart({ organizationId, customerId }: CustomerScope) {
  return axios.post<CreatedCustomerPosCart>(
    `/customers/${customerId}/pos-cart`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCustomerPosHistoryItems({
  queryKey: [{ organizationId, customerId, limit }],
}: CustomerQueryContext<CustomerPosHistoryQueryKeyEntries>) {
  return axios.get<CustomerPosHistoryItem[]>(
    `/customers/${customerId}/pos-history-items`,
    buildAdminRequestConfig({
      organizationId,
      params: limit ? { limit } : {},
    })
  )
}

export async function sendCustomerStatement({
  organizationId,
  customerId,
  email,
}: CustomerScope & { email?: string | null | undefined }) {
  return axios.post<SendCustomerStatementResult>(
    `/customers/${customerId}/statement/email`,
    email ? { email } : {},
    buildAdminRequestConfig({ organizationId })
  )
}

export async function sendCustomerReviewInvite({ organizationId, customerId }: CustomerScope) {
  return axios.post<SendCustomerReviewInviteResult>(
    `/customers/${customerId}/review-invite`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function sendCustomerGoogleReviewInvite({
  organizationId,
  customerId,
}: CustomerScope) {
  return axios.post<SendCustomerReviewInviteResult>(
    `/customers/${customerId}/google-review-invite`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCustomerPaymentStatus({
  organizationId,
  customerId,
  paymentId,
}: CustomerPaymentScope) {
  return axios.get<CustomerPaymentStatus>(
    `/customers/${customerId}/payments/${paymentId}/status`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCustomerPaymentMethods({
  queryKey: [{ organizationId, customerId, paymentEntrySessionId }],
}: CustomerQueryContext<CustomerPaymentMethodsQueryKeyEntries>) {
  return axios.get<CustomerSavedCards>(
    `/customers/${customerId}/payment-methods`,
    buildAdminRequestConfig({
      organizationId,
      params: paymentEntrySessionId ? { payment_entry_session_id: paymentEntrySessionId } : {},
    })
  )
}

export async function createCustomerPaymentMethodSetupIntent({
  organizationId,
  customerId,
}: CustomerScope) {
  return axios.post<CustomerPaymentMethodSetupIntent>(
    `/customers/${customerId}/payment-methods/setup-intent`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function storeCustomerPaymentMethod({
  organizationId,
  customerId,
  setupIntentId,
  paymentMethodId,
  customerAuthorizationConfirmed,
}: Partial<StoreCustomerPaymentMethodInput> & CustomerScope) {
  return axios.post<CustomerSavedCardsResult>(
    `/customers/${customerId}/payment-methods`,
    {
      setup_intent_id: setupIntentId,
      payment_method_id: paymentMethodId,
      customer_authorization_confirmed: customerAuthorizationConfirmed,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteCustomerPaymentMethod({
  organizationId,
  customerId,
  paymentMethodId,
}: CustomerScope & { paymentMethodId?: string }) {
  return axios.delete<CustomerSavedCardsResult>(
    `/customers/${customerId}/payment-methods/${paymentMethodId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

/** What a statement Pay Now starts from for the chosen invoices; the server builds it and writes nothing. */
export async function previewCustomerPaymentSnapshot({
  organizationId,
  customerId,
  ...selection
}: CustomerScope & CustomerPaymentSnapshotSelection) {
  return axios.post<CustomerPaymentSnapshotPreview>(
    `/customers/${customerId}/payment-snapshot`,
    selection,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function prepareCustomerCardPayment({
  organizationId,
  customerId,
  amount,
  customerTotal,
  allocations,
  paymentMethod,
  savePaymentMethod,
  savedPaymentMethodId,
  paymentEntrySessionId,
}: Partial<PrepareCustomerCardPaymentInput> & CustomerScope) {
  return axios.post<PreparedCustomerCardPayment>(
    `/customers/${customerId}/card-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      allocations,
      ...(paymentMethod !== undefined ? { payment_method: paymentMethod } : {}),
      ...(savePaymentMethod !== undefined ? { save_payment_method: savePaymentMethod } : {}),
      ...(savedPaymentMethodId ? { saved_payment_method_id: savedPaymentMethodId } : {}),
      ...(paymentEntrySessionId ? { payment_entry_session_id: paymentEntrySessionId } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function sendCustomerCardPaymentToReader({
  organizationId,
  customerId,
  terminalId,
  amount,
  allocations,
  customerTotal,
  idempotencyKey,
}: Partial<SendCustomerCardPaymentToReaderInput> & CustomerScope) {
  return axios.post<CustomerPaymentResult>(
    `/stripe-terminal/readers/${terminalId}/customers/${customerId}/send-card-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      allocations,
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function cancelCustomerCardPayment({
  organizationId,
  customerId,
  paymentId,
}: CustomerPaymentScope) {
  return axios.delete<CancelledCustomerCardPayment>(
    `/customers/${customerId}/card-payment/${paymentId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Asks the server to settle a statement card payment from what Stripe reports now. */
export async function checkCustomerCardPayment({
  organizationId,
  customerId,
  paymentId,
}: CustomerPaymentScope) {
  return axios.post<CheckedCustomerCardPayment>(
    `/customers/${customerId}/payments/${paymentId}/check`,
    {},
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCustomerCashPayment({
  organizationId,
  customerId,
  amount,
  customerTotal,
  tenderedAmount,
  allocations,
  reference,
  cashDrawerSessionId,
  idempotencyKey,
}: CreateCustomerCashPaymentInput & CustomerScope) {
  return axios.post<CustomerPaymentResult>(
    `/customers/${customerId}/cash-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      ...(tenderedAmount !== undefined ? { tendered_amount: tenderedAmount } : {}),
      allocations,
      ...(reference ? { reference } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      idempotency_key: idempotencyKey,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCustomerCheckPayment({
  organizationId,
  customerId,
  amount,
  customerTotal,
  allocations,
  reference,
  cashDrawerSessionId,
  idempotencyKey,
}: CreateCustomerCheckPaymentInput & CustomerScope) {
  return axios.post<CustomerPaymentResult>(
    `/customers/${customerId}/check-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      allocations,
      ...(reference ? { reference } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      idempotency_key: idempotencyKey,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCustomerRecipientSuggestions({
  queryKey: [{ organizationId, customerId, filters = {} }],
}: CustomerQueryContext<CustomerRecipientSuggestionsQueryKeyEntries>) {
  return axios.get<CustomerRecipientSuggestion[]>(
    `/customers/${customerId}/recipient-suggestions`,
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function createCustomerOccasionReminder({
  organizationId,
  customerId,
  ...values
}: CustomerOccasionReminderInput & CustomerScope) {
  return axios.post<SavedCustomerOccasionReminder>(
    `/customers/${customerId}/occasion-reminders`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCustomerOccasionReminder({
  organizationId,
  customerId,
  reminderId,
  ...values
}: CustomerOccasionReminderInput & CustomerOccasionReminderScope) {
  return axios.put<SavedCustomerOccasionReminder>(
    `/customers/${customerId}/occasion-reminders/${reminderId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteCustomerOccasionReminder({
  organizationId,
  customerId,
  reminderId,
}: CustomerOccasionReminderScope) {
  return axios.delete<DeletedCustomerOccasionReminder>(
    `/customers/${customerId}/occasion-reminders/${reminderId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
